"use client";

import { useEffect, useRef, useState } from "react";
import { Database, Funnel as Filter, ChartLine as LineChart, Layers, Send } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "お問い合わせ",
  description: "ご相談内容を確認し、案件の中心となる法的事項と今後必要となる対応を整理します。",
  icon: Database
}, {
  number: "02",
  title: "分析",
  description: "事実関係、関連資料、適用法令を確認し、案件を進めるための適切な方向性を検討します。",
  icon: Filter
}, {
  number: "03",
  title: "方針策定",
  description: "資料、適用法令、手続上の要件、期限、案件の目的を踏まえ、適切な法的対応方針を構築します。",
  icon: LineChart
}, {
  number: "04",
  title: "実行",
  description: "承認された法律業務を進めます。契約書等の作成、filing、取引手続、hearingへの対応などが含まれる場合があります。",
  icon: Layers
}, {
  number: "05",
  title: "完了",
  description: "委任された業務の範囲に従い、必要な取引、遺産管理、裁判手続、その他の法的手続の完了を目指します。",
  icon: Send
}];
export default function ConsultationProcess() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const handleScroll = () => {
      const node = sectionRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalDistance = rect.height + windowHeight;
      const scrolled = windowHeight - rect.top;
      const ratio = Math.max(0, Math.min(1, scrolled / totalDistance));
      const adjusted = Math.max(0, Math.min(1, (ratio - 0.15) / 0.65));
      setProgress(adjusted);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="3370" id="consultationprocess" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "radial-gradient(hsl(215 50% 15%) 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} aria-hidden="true" />

    <div className="relative max-w-7xl mx-auto">
  <div ref={headerRef} className={`text-center max-w-4xl mx-auto mb-20 md:mb-28 lg:mb-36 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
    <div className="font-mono text-xs md:text-sm tracking-[0.2em] mb-8 text-primary">
      // 02 — ご相談から対応までの流れ
    </div>

    <h2 className="font-serif font-light leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-[80px] text-foreground">
      法的な転機に対応する{" "}
      <em className="italic font-normal text-primary">
        体系的なアプローチ
      </em>
    </h2>

    <p className="font-default mt-8 text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-muted-foreground">
      当事務所では、ビジネス、不動産、Probate、信託関連、Attorney’s Fee
      Mattersについて、まず事実関係と関連資料を整理したうえで、各案件への対応を開始します。
    </p>
  </div>

  <div className="hidden lg:block relative">
    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-border" aria-hidden="true" />

    <div className="absolute left-0 top-1/2 -translate-y-1/2 h-px transition-[width] duration-300 ease-out bg-primary" style={{
          width: `${progress * 100}%`,
          boxShadow: "0 0 12px hsl(215 50% 15% / 0.3)"
        }} aria-hidden="true" />

    <div className="grid grid-cols-5 relative min-h-[560px]">
      {steps.map((step, i) => {
            const Icon = step.icon;
            const isAbove = i % 2 === 0;
            const nodeProgress = (i + 0.5) / steps.length;
            const isActive = progress >= nodeProgress - 0.05;
            return <div key={step.number} data-index={i} className="relative flex flex-col items-center">
            {isAbove && <div className="absolute bottom-1/2 mb-12 w-full px-4 pb-2 text-center">
                <Icon className="mx-auto mb-4 size-6 text-primary" strokeWidth={1.25} />

                <div className="font-mono text-xs tracking-[0.2em] mb-3 text-muted-foreground">
                  {step.number}
                </div>

                <h3 className="font-serif font-normal text-3xl xl:text-4xl mb-4 leading-tight text-foreground">
                  {step.title}
                </h3>

                <p className="font-default text-sm leading-relaxed max-w-[240px] mx-auto text-muted-foreground">
                  {step.description}
                </p>
              </div>}

            <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center">
              <div className="relative rounded-lg flex items-center justify-center bg-foreground" style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "9999px"
                }}>
                <div className="rounded-lg transition-all duration-500 bg-background" style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "9999px",
                    backgroundColor: isActive ? "hsl(43 35% 60%)" : "hsl(30 10% 97%)"
                  }} />
              </div>
            </div>

            {!isAbove && <div className="absolute top-1/2 mt-12 w-full px-4 pt-2 text-center">
                <div className="font-mono text-xs tracking-[0.2em] mb-3 text-muted-foreground">
                  {step.number}
                </div>

                <h3 className="font-serif font-normal text-3xl xl:text-4xl mb-4 leading-tight text-foreground">
                  {step.title}
                </h3>

                <p className="font-default text-sm leading-relaxed max-w-[240px] mx-auto mb-4 text-muted-foreground">
                  {step.description}
                </p>

                <Icon className="mx-auto size-6 text-primary" strokeWidth={1.25} />
              </div>}
          </div>;
          })}
    </div>
  </div>

  <div className="lg:hidden relative">
    <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" aria-hidden="true" />

    <div className="absolute left-[19px] top-0 w-px transition-[height] duration-300 ease-out bg-primary" style={{
          height: `${progress * 100}%`
        }} aria-hidden="true" />

    <div className="space-y-12">
      {steps.map((step, i) => {
            const Icon = step.icon;
            const nodeProgress = (i + 0.5) / steps.length;
            const isActive = progress >= nodeProgress - 0.05;
            return <div key={step.number} data-index={i} className="relative pl-14">
            <div className="absolute left-[10px] top-1 flex items-center justify-center">
              <div className="relative flex items-center justify-center bg-foreground" style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "9999px"
                }}>
                <div className="transition-all duration-500 bg-background" style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "9999px",
                    backgroundColor: isActive ? "hsl(43 35% 60%)" : "hsl(30 10% 97%)"
                  }} />
              </div>
            </div>

            <div className="flex items-start gap-3 mb-3">
              <Icon className="size-5 mt-1 text-primary" strokeWidth={1.25} />

              <div className="font-mono text-xs tracking-[0.2em] pt-1.5 text-muted-foreground">
                {step.number}
              </div>
            </div>

            <h3 className="font-serif font-normal text-3xl mb-3 leading-tight text-foreground">
              {step.title}
            </h3>

            <p className="font-default text-sm leading-relaxed text-muted-foreground">
              {step.description}
            </p>
          </div>;
          })}
    </div>
  </div>

  <div ref={quoteRef} className={`mt-24 md:mt-32 lg:mt-44 max-w-3xl mx-auto text-center transition-all duration-1000 ease-out ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
    <div className="mx-auto mb-8 h-px w-16 bg-secondary" aria-hidden="true" />

    <blockquote className="font-serif italic font-light text-2xl md:text-3xl lg:text-[36px] leading-snug text-foreground">
      「経験と慎重な判断こそが、あらゆる重要な転機を支える基盤です。」
    </blockquote>

    <div className="font-mono text-xs tracking-[0.2em] mt-6 text-muted-foreground">
      — MORRIE I. LEVINE
    </div>
  </div>
    </div>
    </section>;
}